import type { BuildingType } from '../screens/colony/colony-api';

/**
 * Per-building-type artwork registry, tiered by building level — the
 * building equivalent of wall-art.ts's WALL_ART_TIERS, generalized to a map
 * keyed by BuildingType since (unlike the single Bastion Wall) every
 * building type gets its own independent tier list. Ten of the eleven types
 * have art: every one except BASTION_WALL, which is drawn as a perimeter ring
 * instead (wall-art.ts) and so has no per-plot sprite.
 *
 * **Two filename conventions live here, and the difference is meaningful.**
 * A type with a REAL tier ladder names each file by the level it unlocks at,
 * matching wall-art.ts (`he3-reactor-t1` → minLevel 1, `-t4` → 4, and so on).
 * A type on a SINGLE render still carries a `-tN` suffix, which is then just
 * a label — SIEGE_WORKSHOP ships one `-t1` file shown at every level. Read
 * the registry, not the filename, for what is actually gated where.
 *
 * Ladder shapes today: HE3_REACTOR, SCOUT_GARAGE, WATER_ICE_CONDENSER,
 * BARRACKS, BUILDER_CAMP, REGOLITH_MINE and COMPOSITE_FABRICATOR each ship a
 * real 4-tier ladder; COMMAND and STRATEGIC_RESERVE ship 3-tier ladders;
 * SIEGE_WORKSHOP is the one single-render type.
 *
 * Unlike a wall ring (drawn as a perimeter band anchored on a measured
 * "ring" sub-region of the source image, see wall-art.ts), a building tier's
 * source image is the whole building, front-facing, fit to its own plot
 * cell like the circle marker it replaces — so no ringTop/ringBottom
 * bookkeeping is needed here.
 *
 * Asset sizing convention (every tier ships all three, same slug + suffix):
 * `-1k.webp` (fit to 1024px) is `src` below, used by the city view's canvas
 * `drawImage`; `-icon.webp` (fit to 256px — "0.25K") is `iconSrc`, used as
 * the building modal's header icon (colony-screen.ts, replacing the circle
 * fallback when art exists). A `-2k.webp` (fit to 2048px) sibling is
 * generated alongside both for a possible future zoomed/detail view, but
 * nothing loads it yet — no `src2k` field here until something actually
 * consumes it.
 */

export interface BuildingArtTier {
  /** Building level (inclusive) at which this tier's art becomes current. */
  minLevel: number;
  /** 1K — the city view's plot sprite. */
  src: string;
  /** 0.25K — the building modal's header icon. */
  iconSrc: string;
  /**
   * Explicit, manually-set art-direction correction for ONE tier, multiplied
   * into buildingArtScale's per-type value. This is NOT the automatic
   * per-level formula that colony-city-renderer.ts used to apply (that one
   * was removed outright — see resolveBuildingVisual) — it exists only
   * because a specific tier was called out by name as rendering too large
   * once that automatic formula was gone and the source art's own canvas
   * occupancy became the sole scale signal. Omit for a tier that reads fine
   * at the type's default scale; default is 1 (no correction).
   */
  scaleMultiplier?: number;
  /**
   * Explicit, manually-set ground-anchor correction for ONE tier — REPLACES
   * (not multiplies into) colony-city-renderer.ts's buildingArtGroundRatio
   * for this tier specifically, when set. Exists because a type-level
   * ground-ratio override was tried first for Command Center and moved
   * every tier (including ones that were never asked to move); only its L1
   * render was actually called out as needing a different anchor. Omit for
   * a tier that reads fine at the type's default ground ratio.
   */
  groundRatio?: number;
  /**
   * Where the drawn building's visual middle sits, as a fraction down its image frame (default 0.5 = the frame's middle).
   * Only set where the content is bunched away from the middle — the Command Center's level-1 render sits at ~83% down
   * its frame, so anchoring its countdown badge at the frame's middle floated it far above the building.
   */
  contentMidY?: number;
}

export const BUILDING_ART_TIERS: Partial<Record<BuildingType, BuildingArtTier[]>> = {
  // Ordered ascending by minLevel — buildingArtForLevel takes the LAST match.
  // Each source image's own natural proportions (as authored/exported) are
  // trusted by default — resolveBuildingArtDraw draws at a fixed width with
  // height following the source's own aspect ratio. scaleMultiplier (see
  // BuildingArtTier) is the explicit exception: a handful of tiers below are
  // called out by name as reading too large and get one, but it is never
  // applied automatically by level.
  SCOUT_GARAGE: [
    { minLevel: 1, src: '/buildings/scout-garage-t1-1k.webp', iconSrc: '/buildings/scout-garage-t1-icon.webp' },
    // Scaled down: this tier (shown for levels 3-5) read too large on the
    // plot once the per-level auto-scale formula was removed.
    { minLevel: 3, src: '/buildings/scout-garage-t3-1k.webp', iconSrc: '/buildings/scout-garage-t3-icon.webp', scaleMultiplier: 0.8 },
    { minLevel: 6, src: '/buildings/scout-garage-t6-1k.webp', iconSrc: '/buildings/scout-garage-t6-icon.webp' },
    { minLevel: 10, src: '/buildings/scout-garage-t10-1k.webp', iconSrc: '/buildings/scout-garage-t10-icon.webp' },
  ],
  BUILDER_CAMP: [
    { minLevel: 1, src: '/buildings/builder-camp-t1-1k.webp', iconSrc: '/buildings/builder-camp-t1-icon.webp' },
    // Every tier past L1 scaled down — only the L1 render was called out as
    // reading correctly at the type's default scale.
    { minLevel: 4, src: '/buildings/builder-camp-t4-1k.webp', iconSrc: '/buildings/builder-camp-t4-icon.webp', scaleMultiplier: 0.8 },
    { minLevel: 6, src: '/buildings/builder-camp-t6-1k.webp', iconSrc: '/buildings/builder-camp-t6-icon.webp', scaleMultiplier: 0.8 },
    { minLevel: 10, src: '/buildings/builder-camp-t10-1k.webp', iconSrc: '/buildings/builder-camp-t10-icon.webp', scaleMultiplier: 0.8 },
  ],
  BARRACKS: [
    { minLevel: 1, src: '/buildings/barracks-t1-1k.webp', iconSrc: '/buildings/barracks-t1-icon.webp' },
    { minLevel: 4, src: '/buildings/barracks-t4-1k.webp', iconSrc: '/buildings/barracks-t4-icon.webp' },
    { minLevel: 7, src: '/buildings/barracks-t7-1k.webp', iconSrc: '/buildings/barracks-t7-icon.webp' },
    { minLevel: 10, src: '/buildings/barracks-t10-1k.webp', iconSrc: '/buildings/barracks-t10-icon.webp' },
  ],
  // Three-tier ladder (not four) — there is no distinct L10 render; L10 was
  // explicitly asked to look identical to L9, so the L6 tier (which already
  // covers 6-9) simply has no step after it and keeps showing through L10
  // and beyond. The whole type reads too large and is scaled down uniformly
  // via BUILDING_ART_SCALE_OVERRIDES in colony-city-renderer.ts, not a
  // per-tier scaleMultiplier here — this was called out as a whole-building
  // problem, not one specific tier's.
  STRATEGIC_RESERVE: [
    { minLevel: 1, src: '/buildings/strategic-reserve-t1-1k.webp', iconSrc: '/buildings/strategic-reserve-t1-icon.webp' },
    { minLevel: 3, src: '/buildings/strategic-reserve-t3-1k.webp', iconSrc: '/buildings/strategic-reserve-t3-icon.webp' },
    { minLevel: 6, src: '/buildings/strategic-reserve-t6-1k.webp', iconSrc: '/buildings/strategic-reserve-t6-icon.webp' },
  ],
  // Three-tier ladder (not four) — L1 and L3 are fresh renders; L6 reuses the
  // original single-render asset (renamed from command-t10, its pre-ladder
  // name), so it carries the same "legacy tier, no un-cropped source"
  // caveat as the other ladders' one legacy tier (see asset-list.md).
  COMMAND: [
    // Only L1 was called out as needing a different ground anchor — a
    // type-level override was tried first and wrongly moved L3/L6 too.
    //
    // Raised from -0.3 to sit level with L3. The three COMMAND renders put
    // their building in very different parts of their shared 1024x1024
    // canvas (measured content mid-Y: L1 82.6%, L3 59.0%, L6 42.5% down the
    // frame), so an identical groundRatio does NOT put them at the same
    // apparent height — L1's content is bunched near the bottom of its frame
    // and reads as sitting lower than its siblings.
    //
    // Derivation, so this is retunable rather than magic: with
    //   height = radius * BUILDING_ART_BASE_RATIO * scale   (2.6 * 2.255 = 5.863 radii)
    // the drawn content's midpoint lands at
    //   center.y + radius * (groundRatio - 1.020)           for L1's 82.6%
    //   center.y + radius * (groundRatio - 2.404)           for L3's 59.0%
    // L3 at its default 0.9 therefore sits at -1.504 radii; matching that
    // gives L1 a groundRatio of -1.504 + 1.020 = -0.484.
    // scaleMultiplier 1.2 makes L1 render 20% larger — user feedback.
    // groundRatio re-derived: with the new effective scale (2.255 * 1.2 = 2.706),
    // 0.174 * 2.6 * 2.706 = 1.224, so groundRatio = -1.504 + 1.224 = -0.28
    // (keeps L1's visual midpoint aligned with L3's).
    //
    // Then lowered again, to 0.85: at -0.28/-0.08 the building's visual middle (content ~83% down its frame) landed on the
    // TOP edge of its plot ring, and the countdown badge (anchored on that middle) with it. The ring centre sits at
    // -COMMAND_RING_LIFT (0.35) radii, and the content middle at (groundRatio - 1.195) radii, so groundRatio = 0.85
    // puts the building and its badge in the middle of the ring. Retune with that relation if either constant moves.
    { minLevel: 1, src: '/buildings/command-t1-1k.webp', iconSrc: '/buildings/command-t1-icon.webp', scaleMultiplier: 1.2, groundRatio: 0.85, contentMidY: 0.83 },
    { minLevel: 3, src: '/buildings/command-t3-1k.webp', iconSrc: '/buildings/command-t3-icon.webp' },
    { minLevel: 6, src: '/buildings/command-t6-1k.webp', iconSrc: '/buildings/command-t6-icon.webp' },
  ],
  REGOLITH_MINE: [
    { minLevel: 1, src: '/buildings/regolith-mine-t1-1k.webp', iconSrc: '/buildings/regolith-mine-t1-icon.webp' },
    { minLevel: 3, src: '/buildings/regolith-mine-t3-1k.webp', iconSrc: '/buildings/regolith-mine-t3-icon.webp' },
    { minLevel: 6, src: '/buildings/regolith-mine-t6-1k.webp', iconSrc: '/buildings/regolith-mine-t6-icon.webp' },
    { minLevel: 10, src: '/buildings/regolith-mine-t10-1k.webp', iconSrc: '/buildings/regolith-mine-t10-icon.webp' },
  ],
  WATER_ICE_CONDENSER: [
    { minLevel: 1, src: '/buildings/water-ice-condenser-t1-1k.webp', iconSrc: '/buildings/water-ice-condenser-t1-icon.webp' },
    { minLevel: 3, src: '/buildings/water-ice-condenser-t3-1k.webp', iconSrc: '/buildings/water-ice-condenser-t3-icon.webp' },
    { minLevel: 6, src: '/buildings/water-ice-condenser-t6-1k.webp', iconSrc: '/buildings/water-ice-condenser-t6-icon.webp' },
    { minLevel: 9, src: '/buildings/water-ice-condenser-t9-1k.webp', iconSrc: '/buildings/water-ice-condenser-t9-icon.webp' },
  ],
  HE3_REACTOR: [
    { minLevel: 1, src: '/buildings/he3-reactor-t1-1k.webp', iconSrc: '/buildings/he3-reactor-t1-icon.webp' },
    { minLevel: 4, src: '/buildings/he3-reactor-t4-1k.webp', iconSrc: '/buildings/he3-reactor-t4-icon.webp' },
    { minLevel: 7, src: '/buildings/he3-reactor-t7-1k.webp', iconSrc: '/buildings/he3-reactor-t7-icon.webp' },
    { minLevel: 9, src: '/buildings/he3-reactor-t9-1k.webp', iconSrc: '/buildings/he3-reactor-t9-icon.webp' },
  ],
  // The ONLY single-render type in the registry: one building image shown at
  // every level, plus a construction site. It replaces an earlier 3-tier set
  // that was off-style in every way that mattered — olive/rust military
  // industrial instead of the ivory/cobalt/amber house palette, and (the real
  // defect) rendered on an opaque red regolith terrain pad, so its alpha mean
  // was 1.0 against 0.37-0.56 for every other building and it drew as a solid
  // block with black corners.
  //
  // `-t1` keeps the filename convention even though no tier ladder exists
  // here: the suffix is read as "the level this unlocks at", and the registry
  // — not the filename — is what decides. See the two-conventions note at the
  // top of this file.
  SIEGE_WORKSHOP: [
    { minLevel: 1, src: '/buildings/siege-workshop-t1-1k.webp', iconSrc: '/buildings/siege-workshop-t1-icon.webp' },
  ],
  COMPOSITE_FABRICATOR: [
    { minLevel: 1, src: '/buildings/composite-fabricator-t1-1k.webp', iconSrc: '/buildings/composite-fabricator-t1-icon.webp' },
    { minLevel: 4, src: '/buildings/composite-fabricator-t4-1k.webp', iconSrc: '/buildings/composite-fabricator-t4-icon.webp' },
    { minLevel: 7, src: '/buildings/composite-fabricator-t7-1k.webp', iconSrc: '/buildings/composite-fabricator-t7-icon.webp' },
    { minLevel: 10, src: '/buildings/composite-fabricator-t10-1k.webp', iconSrc: '/buildings/composite-fabricator-t10-icon.webp' },
  ],
};

/**
 * "Construction site" art — shown for a building type's plot at level 0
 * (not yet built, so there's nothing for BUILDING_ART_TIERS to match), in
 * place of the plain dashed-circle marker. A staged, themed Mars
 * work-site render rather than a generic placeholder; falls back to the
 * circle marker (same as any type with no art at all) when a type
 * isn't listed here. Independent of BUILDING_ART_TIERS — a type can have
 * one, both, or neither.
 */
export interface BuildingSiteArt {
  src: string;
  iconSrc: string;
  scaleMultiplier?: number;
}

export const BUILDING_SITE_ART: Partial<Record<BuildingType, BuildingSiteArt>> = {
  BARRACKS: { src: '/buildings/barracks-site-1k.webp', iconSrc: '/buildings/barracks-site-icon.webp' },
  SCOUT_GARAGE: { src: '/buildings/scout-garage-site-1k.webp', iconSrc: '/buildings/scout-garage-site-icon.webp' },
  REGOLITH_MINE: { src: '/buildings/regolith-mine-site-1k.webp', iconSrc: '/buildings/regolith-mine-site-icon.webp' },
  HE3_REACTOR: { src: '/buildings/he3-reactor-site-1k.webp', iconSrc: '/buildings/he3-reactor-site-icon.webp' },
  STRATEGIC_RESERVE: { src: '/buildings/strategic-reserve-site-1k.webp', iconSrc: '/buildings/strategic-reserve-site-icon.webp' },
  WATER_ICE_CONDENSER: { src: '/buildings/water-ice-condenser-site-1k.webp', iconSrc: '/buildings/water-ice-condenser-site-icon.webp' },
  BUILDER_CAMP: { src: '/buildings/builder-camp-site-1k.webp', iconSrc: '/buildings/builder-camp-site-icon.webp', scaleMultiplier: 0.5 },
  COMPOSITE_FABRICATOR: { src: '/buildings/composite-fabricator-site-1k.webp', iconSrc: '/buildings/composite-fabricator-site-icon.webp' },
  SIEGE_WORKSHOP: { src: '/buildings/siege-workshop-site-1k.webp', iconSrc: '/buildings/siege-workshop-site-icon.webp' },
  // The Bastion Wall's finished art is a ring drawn around the whole colony
  // perimeter (wall-art.ts), not a single plot sprite — but its construction
  // site is one staged work-site render, so it goes through the ordinary
  // per-plot site channel and appears only in the wall's own grid cell.
  BASTION_WALL: { src: '/buildings/bastion-wall-site-1k.webp', iconSrc: '/buildings/bastion-wall-site-icon.webp', scaleMultiplier: 0.7 },
};

/** The site render for a building type not yet built (level 0), or null if none exists / it's already built. */
export function buildingSiteArtFor(type: BuildingType, level: number): BuildingSiteArt | null {
  if (level > 0) return null;
  return BUILDING_SITE_ART[type] ?? null;
}

/**
 * Icons for buildings whose finished art is not a per-plot sprite: the Bastion Wall is a ring drawn around the whole
 * colony (wall-art.ts), so it has no tier icon — its gate stands in wherever a single picture is needed.
 */
export const BUILDING_ICON_FALLBACK: Partial<Record<BuildingType, string>> = {
  BASTION_WALL: '/buildings/bastion-wall-gate-icon.webp',
};

/** A small picture for any building at any level: its tier icon, else its fallback icon, else its site icon, else null. */
export function buildingIconFor(type: BuildingType, level: number): string | null {
  const tier = buildingArtForLevel(type, level);
  if (tier) return tier.iconSrc;
  return BUILDING_ICON_FALLBACK[type] ?? BUILDING_SITE_ART[type]?.iconSrc ?? null;
}

/** The highest tier whose minLevel is met for this building type, or null if that type has no art yet or isn't built high enough. */
export function buildingArtForLevel(type: BuildingType, level: number): BuildingArtTier | null {
  const tiers = BUILDING_ART_TIERS[type];
  if (!tiers) return null;
  let match: BuildingArtTier | null = null;
  for (const tier of tiers) {
    if (level >= tier.minLevel) match = tier;
  }
  return match;
}

/**
 * Module-level image cache — mirrors wall-art.ts's wallImageCache exactly
 * (loaded once for the page's lifetime, shared in-flight promise per src so
 * a second caller arriving mid-load gets the same eventual image instead of
 * starting a duplicate request).
 */
const buildingImageCache = new Map<string, HTMLImageElement>();
const buildingImageLoading = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

/** Already-loaded image for a tier's src, if any — a synchronous, cache-only lookup. */
export function loadedBuildingImage(src: string): HTMLImageElement | undefined {
  return buildingImageCache.get(src);
}

/**
 * Kicks off loading every known tier's art that isn't already cached, and
 * calls `onLoaded` once that tier becomes available. No retry on failure —
 * a tier simply never becomes available in the cache, and drawCityScene
 * already treats an unavailable tier as "fall back to the circle
 * marker", same as wall-art.ts's preloadWallArt.
 */
export function preloadBuildingArt(onLoaded: (tier: { src: string }) => void): void {
  const allArt: { src: string }[] = [
    ...Object.values(BUILDING_ART_TIERS).flatMap((tiers) => tiers ?? []),
    ...Object.values(BUILDING_SITE_ART),
  ];
  for (const tier of allArt) {
    if (buildingImageCache.has(tier.src)) continue;

    const inFlight = buildingImageLoading.get(tier.src);
    if (inFlight) {
      inFlight.then(() => onLoaded(tier)).catch(() => {});
      continue;
    }

    const promise = loadImage(tier.src);
    buildingImageLoading.set(tier.src, promise);
    promise
      .then((img) => {
        buildingImageCache.set(tier.src, img);
        onLoaded(tier);
      })
      .catch(() => {
        /* no fallback — see doc comment above */
      })
      .finally(() => buildingImageLoading.delete(tier.src));
  }
}

/** Test seam — the cache is process-wide and would leak between cases. */
export function clearBuildingArtCache(): void {
  buildingImageCache.clear();
  buildingImageLoading.clear();
}
